import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";

export type BrandOption = { id: string; name: string };

export function BrandSelect({
  value,
  onChange,
  brands,
  onAddBrand,
}: {
  value: string;
  onChange: (id: string) => void;
  brands: BrandOption[];
  onAddBrand?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const active = brands.find((b) => b.id === value);

  return (
    <div ref={ref} className="relative mt-1 inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-base font-semibold text-slate-900 hover:border-slate-400"
      >
        {active?.name ?? "Select a brand"}
        <ChevronDown className="h-4 w-4 text-slate-500" />
      </button>
      {open && (
        <div className="absolute left-0 top-full z-10 mt-1 w-56 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg">
          {brands.map((b) => (
            <button
              key={b.id}
              type="button"
              onClick={() => {
                onChange(b.id);
                setOpen(false);
              }}
              className="flex w-full items-center justify-between px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
            >
              <span className="font-medium">{b.name}</span>
              {value === b.id && <Check className="h-4 w-4 text-emerald-600" />}
            </button>
          ))}
          {onAddBrand && (
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onAddBrand();
              }}
              className="block w-full border-t border-slate-100 px-3 py-2 text-left text-sm text-slate-500 hover:bg-slate-50"
            >
              + Add brand
            </button>
          )}
        </div>
      )}
    </div>
  );
}
